import { useEffect, useState } from "react";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonInput,
  IonIcon,
  IonToggle,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { searchOutline, closeCircleOutline } from "ionicons/icons";
import type { BrowseFilter } from "../../hooks/useGroupBrowse";
import { useDictionary } from "../../hooks/useDictionary";

import "./GroupSearch.css";

interface GroupSearchProps {
  filter: BrowseFilter;
  onFilterChange: (filter: BrowseFilter) => void;
}

interface GroupTypeEntry {
  Name?: string;
  TypeId?: number;
  [key: string]: unknown;
}

const GroupSearch: React.FC<GroupSearchProps> = ({ filter, onFilterChange }) => {
  const { dictionary: groupTypes } = useDictionary("GroupTypes");

  // The input's text is local state updated synchronously on every keystroke;
  // only its propagation into filter.search (and so the query key) is
  // debounced. Feeding the debounce-lagged filter.search back in as the
  // input's value is what used to overwrite keystrokes when a results
  // re-render landed mid-typing.
  const [searchText, setSearchText] = useState(filter.search);

  useEffect(() => {
    if (searchText === filter.search) return;
    const timer = setTimeout(() => {
      onFilterChange({ ...filter, search: searchText });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText, filter, onFilterChange]);

  const updateFilter = (partial: Partial<BrowseFilter>) => {
    onFilterChange({ ...filter, ...partial });
  };

  const clearSearch = () => {
    setSearchText("");
    updateFilter({ search: "" });
  };

  return (
    <div className="group-search">
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol size="12" sizeMd="4">
            <IonItem lines="none" className="search-input-item">
              <IonIcon slot="start" icon={searchOutline} color="medium" />
              <IonInput
                placeholder="Search groups..."
                value={searchText}
                onIonInput={(e) => setSearchText(e.detail.value ?? "")}
              />
              {searchText && (
                <IonIcon
                  icon={closeCircleOutline}
                  slot="end"
                  color="medium"
                  className="search-clear"
                  onClick={clearSearch}
                />
              )}
            </IonItem>
          </IonCol>
          <IonCol size="6" sizeMd="3">
            <IonSelect
              placeholder="All Types"
              multiple
              value={filter.type}
              onIonChange={(e) =>
                updateFilter({ type: e.detail.value || [] })
              }
              className="type-select"
              interface="popover"
            >
              {(groupTypes as GroupTypeEntry[]).map((t) => (
                <IonSelectOption key={t.TypeId} value={t.TypeId}>
                  {t.Name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonCol>
          <IonCol size="6" sizeMd="2.5">
            <IonItem lines="none" className="toggle-item">
              <IonLabel>Subscribed</IonLabel>
              <IonToggle
                checked={filter.subscribedOnly}
                onIonChange={(e) =>
                  updateFilter({ subscribedOnly: e.detail.checked })
                }
                color="secondary"
              />
            </IonItem>
          </IonCol>
          <IonCol size="6" sizeMd="2.5">
            <IonItem lines="none" className="toggle-item">
              <IonLabel>Active Only</IonLabel>
              <IonToggle
                checked={filter.active_only}
                onIonChange={(e) =>
                  updateFilter({ active_only: e.detail.checked })
                }
                color="secondary"
              />
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
};

export default GroupSearch;
